import React, { useEffect, useMemo, useState } from "react";
import SiteLayout from "../components/layout/SiteLayout";
import { api, resolveAssetUrl } from "../lib/api";
import { useSettings } from "../lib/settings";
import { PlayCircle, Search } from "lucide-react";

function youtubeId(url = "") {
  const m =
    url.match(/youtu\.be\/([\w-]{6,})/) ||
    url.match(/youtube\.com\/watch\?v=([\w-]{6,})/) ||
    url.match(/youtube\.com\/embed\/([\w-]{6,})/);
  return m ? m[1] : null;
}

export default function SermonsPage() {
  const [sermons, setSermons] = useState([]);
  const [active, setActive] = useState(null);
  const [q, setQ] = useState("");
  const { settings } = useSettings();

  useEffect(() => {
    api.get("/sermons").then((r) => {
      setSermons(r.data);
      setActive(r.data[0] || null);
    });
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return sermons;
    const term = q.toLowerCase();
    return sermons.filter(
      (s) =>
        s.title.toLowerCase().includes(term) ||
        s.speaker.toLowerCase().includes(term) ||
        (s.scripture || "").toLowerCase().includes(term) ||
        (s.series || "").toLowerCase().includes(term),
    );
  }, [sermons, q]);

  const activeId = active && youtubeId(active.youtube_url);

  return (
    <SiteLayout>
      {/* Banner */}
      <section data-testid="sermons-hero" className="relative">
        <div className="absolute inset-0">
          <img src={resolveAssetUrl(settings.sermons_hero_image_url)} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[hsl(var(--ink))]/55" />
        </div>
        <div className="relative container-x py-28 lg:py-36">
          <div className="eyebrow text-[hsl(var(--linen))]/80 mb-4">{settings.sermons_eyebrow}</div>
          <h1 className="display text-5xl lg:text-7xl text-[hsl(var(--linen))]">{settings.sermons_headline}</h1>
          <p className="mt-5 max-w-2xl text-[hsl(var(--linen))]/80 leading-relaxed">
            {settings.sermons_intro}
          </p>
        </div>
      </section>

      <section className="container-x py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Player */}
          <div className="lg:col-span-8">
            {activeId ? (
              <div className="youtube-frame rounded-sm overflow-hidden border border-[hsl(var(--line))]">
                <iframe
                  data-testid="sermon-player"
                  width="100%"
                  height="100%"
                  src={`https://www.youtube.com/embed/${activeId}`}
                  title={active.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <div className="youtube-frame bg-[hsl(var(--sand))] border border-[hsl(var(--line))] flex items-center justify-center text-[hsl(var(--ink-muted))]">
                No sermons yet
              </div>
            )}

            {active && (
              <div className="mt-8">
                <div className="eyebrow mb-3">{active.series || "Standalone"}</div>
                <h2 className="display text-3xl lg:text-4xl">{active.title}</h2>
                <div className="mt-3 text-sm text-[hsl(var(--ink-muted))]">
                  {active.speaker} {active.scripture && <>· <span className="italic">{active.scripture}</span></>} ·{" "}
                  {new Date(active.date).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
                </div>
                <p className="mt-6 text-[hsl(var(--ink-muted))] leading-relaxed max-w-2xl">{active.description}</p>
              </div>
            )}
          </div>

          {/* List */}
          <div className="lg:col-span-4">
            <div className="relative mb-6">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--ink-muted))]" />
              <input
                data-testid="sermon-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search sermons…"
                className="w-full pl-9 pr-3 py-3 rounded-sm bg-[hsl(var(--linen))] border border-[hsl(var(--line))] text-sm focus:outline-none focus:border-[hsl(var(--terracotta))]"
              />
            </div>
            <div data-testid="sermons-list" className="divide-y divide-[hsl(var(--line))] border-y border-[hsl(var(--line))]">
              {filtered.map((s) => {
                const isActive = active?.id === s.id;
                return (
                  <button
                    key={s.id}
                    data-testid={`sermon-item-${s.id}`}
                    onClick={() => setActive(s)}
                    className={`w-full text-left py-5 flex gap-4 lift ${isActive ? "bg-[hsl(var(--sand))] px-4" : "px-2"}`}
                  >
                    <PlayCircle size={18} className="mt-1 shrink-0 text-[hsl(var(--terracotta))]" />
                    <div className="flex-1">
                      <div className="display text-xl">{s.title}</div>
                      <div className="mt-1 text-xs text-[hsl(var(--ink-muted))]">
                        {s.speaker} ·{" "}
                        {new Date(s.date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                      </div>
                      {s.scripture && (
                        <div className="mt-0.5 text-xs italic text-[hsl(var(--ink-muted))]">{s.scripture}</div>
                      )}
                    </div>
                  </button>
                );
              })}
              {!filtered.length && (
                <div className="py-6 text-sm text-[hsl(var(--ink-muted))]">No sermons match your search.</div>
              )}
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
